import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Plus, Edit, Trash2, X, Save, Wind, Lock, Unlock } from 'lucide-react';

export default function ManageBreathing() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [exercises, setExercises] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '', description: '', inhale: 4, hold: 4, exhale: 4, difficulty: 'beginner', is_premium: false
  });

  useEffect(() => { loadExercises(); }, []);

  const loadExercises = async () => {
    try {
      const response = await axios.get(`${API}/breathing-exercises`);
      setExercises(response.data);
    } catch (error) { console.error('Failed to load exercises:', error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/breathing-exercises`, formData, { headers: { Authorization: `Bearer ${token}` } });
      setFormData({ name: '', description: '', inhale: 4, hold: 4, exhale: 4, difficulty: 'beginner', is_premium: false });
      setShowForm(false);
      loadExercises();
    } catch (error) { alert('Failed to create exercise'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Wind className="w-6 h-6" style={{ color: currentColors.primary }} />
          <h2 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>Manage Breathing</h2>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="cosmic-btn flex items-center gap-2">
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span>{showForm ? 'Cancel' : 'Add New'}</span>
        </button>
      </div>

      {showForm && (
        <div className="glass-card p-6">
          <h3 className="text-xl font-cinzel mb-4" style={{ color: currentColors.text }}>Add Breathing Exercise</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="cosmic-input w-full" required />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Difficulty</label>
                <select value={formData.difficulty} onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })} className="cosmic-input w-full">
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Description *</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="cosmic-input w-full" rows="2" required />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Inhale (sec)</label>
                <input type="number" value={formData.inhale} onChange={(e) => setFormData({ ...formData, inhale: parseInt(e.target.value) })} className="cosmic-input w-full" min="1" />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Hold (sec)</label>
                <input type="number" value={formData.hold} onChange={(e) => setFormData({ ...formData, hold: parseInt(e.target.value) })} className="cosmic-input w-full" min="0" />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Exhale (sec)</label>
                <input type="number" value={formData.exhale} onChange={(e) => setFormData({ ...formData, exhale: parseInt(e.target.value) })} className="cosmic-input w-full" min="1" />
              </div>
            </div>
            
            {/* Premium/Free Toggle */}
            <div 
              className="p-4 rounded-lg flex items-center justify-between"
              style={{ 
                backgroundColor: formData.is_premium ? `${currentColors.accent}10` : 'rgba(34, 197, 94, 0.1)',
                border: `1px solid ${formData.is_premium ? `${currentColors.accent}30` : 'rgba(34, 197, 94, 0.3)'}`
              }}
            >
              <div className="flex items-center gap-3">
                {formData.is_premium ? (
                  <Lock className="w-5 h-5" style={{ color: currentColors.accent }} />
                ) : (
                  <Unlock className="w-5 h-5 text-green-500" />
                )}
                <div>
                  <p className="font-medium" style={{ color: currentColors.text }}>
                    {formData.is_premium ? 'Premium Content' : 'Free Content'}
                  </p>
                  <p className="text-xs" style={{ color: currentColors.textMuted }}>
                    {formData.is_premium 
                      ? 'Only users with premium access can use this exercise' 
                      : 'This exercise is available to all users'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_premium: !formData.is_premium })}
                className="relative w-14 h-7 rounded-full transition-all"
                data-testid="breathing-premium-toggle"
                style={{ 
                  backgroundColor: formData.is_premium ? currentColors.accent : '#22c55e'
                }}
              >
                <div 
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                    formData.is_premium ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>

            <button type="submit" className="cosmic-btn w-full flex items-center justify-center gap-2">
              <Save className="w-5 h-5" /><span>Create Exercise</span>
            </button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {exercises.map((exercise) => (
          <div key={exercise.id} className="glass-card p-4">
            <div className="flex justify-between items-start mb-2">
              <h3 style={{ color: currentColors.text }} className="font-cinzel">{exercise.name}</h3>
              {exercise.is_premium ? (
                <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: `${currentColors.accent}20`, color: currentColors.accent }}>
                  <Lock className="w-3 h-3" /> Premium
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>
                  <Unlock className="w-3 h-3" /> Free
                </span>
              )}
            </div>
            <p className="text-sm mb-3" style={{ color: currentColors.textMuted }}>{exercise.description}</p>
            <div className="flex gap-2 text-xs mb-2">
              <span className="px-2 py-1 rounded" style={{ backgroundColor: `${currentColors.accent}20`, color: currentColors.accent }}>In: {exercise.inhale}s</span>
              <span className="px-2 py-1 rounded" style={{ backgroundColor: `${currentColors.secondary}20`, color: currentColors.secondary }}>Hold: {exercise.hold}s</span>
              <span className="px-2 py-1 rounded" style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}>Out: {exercise.exhale}s</span>
            </div>
            <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}>{exercise.difficulty}</span>
          </div>
        ))}
        {exercises.length === 0 && (
          <div className="col-span-full text-center py-8" style={{ color: currentColors.textMuted }}>No exercises found.</div>
        )}
      </div>
    </div>
  );
}
